import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Avatar,
  ContactLocation,
  ContactMail,
  ContactPhone,
  ContactWebsite,
  PersonalDriverLicense,
  PersonalFood,
  useRichTextEditor,
} from '@producer/ui-kit';
import { observer } from 'mobx-react-lite';
import { TrashIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import { EditorContent } from '@tiptap/react';

import { List } from '../../features/contacts/components/list';
import { EditButton } from '../../components/icons/EditButton';
import { DeleteDialog } from '../../components/dialogs/DeleteDialog';
import { Contact } from '../../app/entities/contact';

import { DropdownMenu } from '../../components/modals/dropdown-menu/DropdownMenu';
import { uiStore } from '../../core/stores/ui-store';
import { FormBuilderModal } from '../../components/form-builder-modal/form-builder-modal';
import { buildContactForm } from './contact-form.definition';
import { useResponsive } from '../../hooks/useResponsive';
import { EditorBubbleMenu } from '../../components/rich-editor/editor-bubble-menu';
import { openModal } from '../../core/modal/open-modal';
import { formatPicture } from '../../core/services/image.service';
import { Frame } from '../../lib/utils/transform-image';

import styles from './contact-detail.screen.module.scss';
export const ContactDetailScreen: React.FC = observer(() => {
  const { t } = useTranslation('contact');
  const navigate = useNavigate();
  const [file, setFile] = useState<File>();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { isDesktop } = useResponsive();

  const { contactId } = useParams() as { contactId: string };
  const contact = Contact.getOne(contactId)!;

  const { editor, isTextSelected } = useRichTextEditor({
    key: contactId,
    value: contact.notes || '',
    onBlur: (text) => {
      contact.update({ notes: text });
    },
  });

  const [updateModalKey, setUpdateModalKey] = useState(contact?._id);

  useEffect(() => {
    if (contact?._id) {
      // To rerender the update form modal
      setUpdateModalKey(contact._id + Math.random());
    }
  }, [contact?._id]);

  useEffect(() => {
    uiStore.showBack = true;
  }, []);

  if (!contact) {
    return <div />;
  }

  const getContactLocation = () => {
    let location = '';
    if (contact.city) {
      location += contact.city;
    }
    if (contact.city && contact.country) {
      location += ', ';
    }
    if (contact.country) {
      location += contact.country;
    }
    return location;
  };

  const info = {
    contacts: [
      {
        img: <ContactLocation />,
        name: getContactLocation(),
      },
      { img: <ContactPhone />, name: contact.phone || '' },
      { img: <ContactMail />, name: contact.email || '' },
      { img: <ContactWebsite />, name: contact.website || '' },
    ],
    personal: [
      { img: <PersonalFood />, name: contact.foodPreferences || '' },
      { img: <PersonalDriverLicense />, name: contact.driverLicense || '' },
    ],
  };

  const handleContactDelete = async () => {
    await contact.delete();

    navigate('..');
  };

  const menuItems = [
    {
      onSelect: () => setIsEditModalOpen(true),
      title: 'Edit',
      icon: <EditButton />,
    },
    {
      onSelect: () =>
        openModal(DeleteDialog, {
          title: 'Delete contact ?',
          text: `Are you sure you want to delete ${contact.fullName} from your contacts? This action is irreversible!`,
          onSubmit: handleContactDelete,
        }),
      title: 'Delete',
      icon: <TrashIcon />,
      type: 'danger' as const,
    },
  ];

  const handleSubmitContact = async (data: any) => {
    let frame: Frame | undefined = undefined;

    if (file) {
      const options = { width: 600, height: 600 };
      frame = await formatPicture(file, options);
    }

    await contact.update({ file: frame, ...data });

    // To rerender the update form modal
    setUpdateModalKey(contact._id + Math.random());
  };

  const updateContactFormDefinition = buildContactForm(false, t, setFile);

  return (
    <React.Fragment>
      <div className={styles['info-container']}>
        <div className={styles['user-avatar-container']}>
          <Avatar src={contact.avatar} alt="photo" size="fill" initials={contact.initials} />
        </div>

        <div className={styles['actions-container']}>
          <h2 className={styles['name']}>{contact.fullName}</h2>

          <DropdownMenu items={menuItems} />
        </div>

        {info.contacts.some((item) => !!item.name) && (
          <List title="Contact information" list={info.contacts} />
        )}

        {info.personal.some((item) => !!item.name) && (
          <List
            className={styles['list-wrapper']}
            title="Personal information"
            list={info.personal}
          />
        )}

        {!isDesktop && (
          <div className={styles['notes-wrapper']} key={`${contactId}-editor`}>
            <h2 className={styles['notes-header']}>Notes</h2>
            <EditorContent
              className={`${styles['notes-input']} ${'notes-textarea'}`}
              editor={editor}
              placeholder="Write notes here"
            />
          </div>
        )}
      </div>

      {isDesktop && (
        <div className={styles['notes-wrapper']}>
          <h2 className={styles['notes-header']}>Notes</h2>
          {editor && isTextSelected && <EditorBubbleMenu editor={editor} />}
          <EditorContent
            className={`${styles['notes-input']} ${'notes-textarea'}`}
            key={`${contactId}-editor`}
            editor={editor}
            placeholder="Write notes here"
          />
        </div>
      )}

      {isEditModalOpen && (
        <FormBuilderModal
          key={updateModalKey}
          model="contact"
          definition={updateContactFormDefinition}
          initialValues={contact}
          onSubmit={handleSubmitContact}
          onCancel={() => setIsEditModalOpen(false)}
        />
      )}
    </React.Fragment>
  );
});
